/**
 * cableTraceFarEnd.logic.ts — Pure functional domain logic for Far-End Cable Navigation.
 *
 * Implements:
 * - Identification of far-end (remote endpoint) hop in an end-to-end cable trace.
 * - Validation of jump feasibility (remote rack_id and port_id availability).
 * - Construction of deep link URLs with highlightPort and origin return tracking.
 * - Hop header formatting with equipment and rack location names.
 * - Parsing of return-to-origin rack parameters from URL query strings.
 */

export interface TraceConnectionInfo {
  type: 'cable' | 'internal';
  cable_id?: string;
  cable_type?: string;
  color?: string;
}

export interface TraceHopItem {
  port_id: string;
  port_name: string;
  equipment_id: string;
  equipment_name: string;
  equipment_type: string;
  rack_id?: number | string | null;
  rack_name?: string | null;
  rack_row_index?: number | null;
  rack_pos_in_row?: number | null;
  connection?: TraceConnectionInfo | null;
}

export interface ReturnRackInfo {
  returnRackId: string | null;
  returnRackName: string | null;
  highlightPortId: string | null;
}

/**
 * Returns the far-end (last) hop in a multi-hop trace if available.
 */
export function getFarEndHop(trace: TraceHopItem[]): TraceHopItem | null {
  if (!Array.isArray(trace) || trace.length < 2) {
    return null;
  }
  const last = trace[trace.length - 1];
  if (!last || !last.port_id) {
    return null;
  }
  return last;
}

/**
 * Checks if navigation to the far end is possible (last hop has a valid rack_id).
 */
export function canJumpToFarEnd(
  trace: TraceHopItem[],
  _currentRackId?: number | string | null
): boolean {
  const farEnd = getFarEndHop(trace);
  if (!farEnd || farEnd.rack_id == null) {
    return false;
  }
  const rId = farEnd.rack_id;
  if (typeof rId === 'number' && !Number.isFinite(rId)) {
    return false;
  }
  if (typeof rId === 'string' && !rId.trim()) {
    return false;
  }
  return true;
}

/**
 * Builds the navigation URL to jump to the far-end rack and highlight the remote port.
 * Optionally includes returnRack and returnRackName query params to allow 1-click return.
 */
export function buildFarEndNavigationUrl(
  dcCode: string,
  farEndHop: TraceHopItem,
  currentRackId?: number | string | null,
  currentRackName?: string | null
): string {
  if (!farEndHop || farEndHop.rack_id == null) {
    return '';
  }
  const rId = farEndHop.rack_id;
  if (typeof rId === 'number' && !Number.isFinite(rId)) {
    return '';
  }
  if (typeof rId === 'string' && !rId.trim()) {
    return '';
  }

  const cleanDc = encodeURIComponent((dcCode || '').trim().replace(/^\/+|\/+$/g, ''));
  const targetRack = encodeURIComponent(String(rId).trim());
  const params = new URLSearchParams();

  if (farEndHop.port_id && farEndHop.port_id.trim()) {
    params.set('highlightPort', farEndHop.port_id.trim());
  }

  if (currentRackId != null && String(currentRackId).trim() !== String(rId).trim()) {
    params.set('returnRack', String(currentRackId).trim());
    if (currentRackName && currentRackName.trim()) {
      params.set('returnRackName', currentRackName.trim());
    }
  }

  const queryString = params.toString();
  return `/${cleanDc}/racks/${targetRack}${queryString ? `?${queryString}` : ''}`;
}

/**
 * Formats the rack and equipment name for trace hop cards.
 * E.g. "Server Alpha · Стойка A04" or "Server Alpha" if rack_name is missing.
 */
export function formatHopRackLabel(
  equipmentName: string,
  rackName?: string | null
): string {
  const cleanEq = (equipmentName || 'Оборудование').trim();
  const cleanRack = (rackName || '').trim();
  if (!cleanRack) {
    return cleanEq;
  }
  return `${cleanEq} · Стойка ${cleanRack}`;
}

/**
 * Parses return-to-rack information and port highlighting from URL search parameters.
 */
export function parseReturnRackInfo(
  searchParams: URLSearchParams | { get: (key: string) => string | null }
): ReturnRackInfo {
  const returnRackId = searchParams.get('returnRack');
  const returnRackName = searchParams.get('returnRackName');
  const highlightPortId = searchParams.get('highlightPort');

  return {
    returnRackId: returnRackId && returnRackId.trim() ? returnRackId.trim() : null,
    returnRackName: returnRackName && returnRackName.trim() ? returnRackName.trim() : null,
    highlightPortId: highlightPortId && highlightPortId.trim() ? highlightPortId.trim() : null,
  };
}
